/**
 * Central DataLayer Utility for Micasa Sucasa
 * Handles pushing events to window.dataLayer for GTM/GA4/Meta
 */

import { analyticsConfig, initializeMetaPixel, initializeGA4 } from '../config/analytics';

// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

// Initialize tracking on module load
if (typeof window !== 'undefined') {
  initializeMetaPixel();
  initializeGA4();
}

/**
 * Push an event to the dataLayer
 * @param {string} eventName - The name of the event (e.g., 'add_to_cart')
 * @param {object} ecommerceData - The ecommerce object (optional)
 * @param {object} userData - User data for matching (optional)
 * @param {object} customData - Any other custom parameters
 */
export const pushEvent = (eventName, ecommerceData = null, userData = null, customData = {}) => {
  const eventPayload = {
    event: eventName,
    ...customData
  };

  // Add ecommerce data if present (clearing previous object first is recommended practice for GA4)
  if (ecommerceData) {
    // Push null to clear previous ecommerce object (GA4 best practice)
    window.dataLayer.push({ ecommerce: null });
    eventPayload.ecommerce = ecommerceData;

    // FLATTEN DATA FOR META PIXEL / GTM COMPATIBILITY
    // This ensures properties like value, currency, items are available at the root
    Object.assign(eventPayload, ecommerceData);

    // Explicitly hoist item-level properties for single-item events (add_to_cart, view_item)
    // This allows GTM variables like {{DL - Product ID}} to work easily
    if (ecommerceData.items && ecommerceData.items.length > 0) {
      const firstItem = ecommerceData.items[0];
      if (firstItem.product_id) eventPayload.product_id = firstItem.product_id;
      if (firstItem.price) eventPayload.price = firstItem.price;
      if (firstItem.quantity) eventPayload.quantity = firstItem.quantity;
    }
  }

  // Add user data if present (hashed emails/phones should be handled here or in GTM)
  if (userData) {
    eventPayload.user = {
      ...userData
    };
  }

  window.dataLayer.push(eventPayload);
  
  // Log for debugging (remove in prod if needed)
  console.log(`[DataLayer] Pushed: ${eventName}`, eventPayload);
};

/**
 * Standardize User Data
 * @param {object} user - User object from cookies/state
 */
export const formatUserData = (user) => {
  if (!user) return null;
  return {
    user_id: user.uid || user.id || null, // Internal User ID
    email: user.email || undefined,     // Will be hashed by GTM
    phone: user.mobile || user.phone || undefined // Will be hashed by GTM
  };
};

/**
 * Helper to calculate cart total value
 * @param {Array} items - Array of cart items
 */
export const calculateCartTotal = (items) => {
  if (!items || !Array.isArray(items)) return 0;
  return items.reduce((acc, item) => acc + (Number(item.price) * Number(item.pqty || item.quantity || 1)), 0);
};


/**
 * Track a purchase event
 * @param {string} transactionId - The unique order/transaction ID
 * @param {number} value - The total value of the transaction
 * @param {string} currency - The currency code (e.g., 'INR')
 * @param {Array} items - Array of items purchased
 */
export const trackPurchase = (transactionId, value, currency, items) => {
  const purchaseData = {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items
  };
  pushEvent('purchase', purchaseData);
};

